import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useToast } from '../../lib/store';
import type { RosterResponse, WarRosterEntry } from '../../lib/types';
import { Button, ErrorState, Panel } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The war team builder — design PART 9 and 18.
 *
 * Two things it deliberately makes visible, because they are the decisions that
 * actually matter and both are invisible in the ordinary collection view:
 *
 *   - **Type coverage.** Teams lock at war start, so a player cannot counter
 *     each opponent individually and has to build something balanced. Showing
 *     what the team is collectively weak to is the difference between that
 *     being a real decision and a hidden one.
 *   - **A Pokemon's war record.** A veteran should be recognisable while
 *     picking, not only on its own page.
 *
 * Stats shown here are resolved server-side with the same `computeStat` the
 * battle engine uses, so what the builder promises is what fights.
 */

const STYLES = [
  { key: 'balanced', label: 'Balanced', help: 'Sensible choices in most situations.' },
  { key: 'aggressive', label: 'Aggressive', help: 'Favours damage over safety.' },
  { key: 'defensive', label: 'Defensive', help: 'Heals and shores up rather than trading.' },
  { key: 'speed', label: 'Speed', help: 'Leans on going first.' },
  { key: 'support', label: 'Support', help: 'Status and debuffs before damage.' },
] as const;

export function TeamBuilder({
  warId,
  onEnlisted,
}: {
  warId: string;
  onEnlisted: () => void;
}) {
  const { push } = useToast();
  const state = useAsync<RosterResponse>(() => api.get('/wars/roster'));
  const [picked, setPicked] = useState<string[]>([]);
  const [style, setStyle] = useState('balanced');
  const [busy, setBusy] = useState(false);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Reading your collection…</p>;

  const { roster, teamSize } = state.data;
  const team = picked
    .map((id) => roster.find((entry) => entry.id === id))
    .filter((entry): entry is WarRosterEntry => Boolean(entry));

  function toggle(id: string) {
    setPicked((current) =>
      current.includes(id)
        ? current.filter((entry) => entry !== id)
        : current.length >= teamSize
          ? current
          : [...current, id],
    );
  }

  async function enlist() {
    setBusy(true);
    try {
      await api.post('/wars/enlist', { warId, pokemonIds: picked, style });
      push('success', 'Enlisted. Your team is locked when the war begins.', '⚔');
      onEnlisted();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not enlist.');
    } finally {
      setBusy(false);
    }
  }

  const avgLevel =
    team.length > 0 ? Math.round(team.reduce((sum, m) => sum + m.level, 0) / team.length) : 0;

  return (
    <Panel title="⚔ Build your war team" hint={`${picked.length} / ${teamSize}`}>
      <p className="tiny muted">
        Teams lock the moment the war starts, so build something that copes with anything rather
        than countering one opponent. Levels matter a great deal — you are matched against Guilds of
        similar level, so bring your best six.
      </p>

      {team.length > 0 && (
        <div className="pxwell" style={{ padding: 'var(--gap-sm)', marginTop: 'var(--gap-sm)' }}>
          <div className="row row--between tiny">
            <strong>Your team</strong>
            <span className="muted">average level {avgLevel}</span>
          </div>
          <div className="row row--wrap" style={{ gap: 4, marginTop: 4 }}>
            {team.map((member) => (
              <button
                key={member.id}
                type="button"
                className="chip chip--sm"
                onClick={() => toggle(member.id)}
                title="Remove"
              >
                {member.isShiny && '✦ '}
                {member.name} Lv{member.level} ✕
              </button>
            ))}
          </div>
          <Coverage team={team} />
        </div>
      )}

      <div className="field" style={{ marginTop: 'var(--gap-sm)' }}>
        <label className="field__label" htmlFor="war-style">
          Battle style
        </label>
        <select
          id="war-style"
          className="select"
          value={style}
          onChange={(event) => setStyle(event.target.value)}
        >
          {STYLES.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label} — {entry.help}
            </option>
          ))}
        </select>
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6, marginTop: 'var(--gap-sm)' }}
      >
        {roster.map((entry) => {
          const chosen = picked.includes(entry.id);
          return (
            <button
              key={entry.id}
              type="button"
              className={`pxframe war-pick ${chosen ? 'war-pick--chosen' : ''}`}
              onClick={() => toggle(entry.id)}
              disabled={!chosen && picked.length >= teamSize}
            >
              <img className="pixel" src={entry.sprite} alt="" width={40} height={40} />
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div className="tiny" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {entry.isShiny && <span style={{ color: 'var(--gold-dark)' }}>✦ </span>}
                  {entry.name}
                </div>
                <div className="tiny muted">
                  Lv {entry.level} · {formatNumber(entry.statTotal)} total
                </div>
                {entry.warBattles > 0 && (
                  <div className="tiny muted">
                    ⚔ {entry.warWins}/{entry.warBattles}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <Button
        variant="primary"
        block
        loading={busy}
        disabled={picked.length === 0}
        onClick={() => void enlist()}
        style={{ marginTop: 'var(--gap-sm)' }}
      >
        {picked.length === 0 ? 'Pick at least one' : `Enlist with ${picked.length}`}
      </Button>
    </Panel>
  );
}

/**
 * What the team is collectively weak to.
 *
 * Computed from the *best* resistance any member has, because the battle AI
 * switches: a team is only genuinely weak to Electric if every member takes
 * extra from it. Showing per-member weaknesses would flag a problem the team
 * does not actually have.
 */
function Coverage({ team }: { team: WarRosterEntry[] }) {
  const CHART: Record<string, Record<string, number>> = {
    fire: { grass: 2, ice: 2, bug: 2, steel: 2, water: 0.5, rock: 0.5, fire: 0.5, dragon: 0.5 },
    water: { fire: 2, ground: 2, rock: 2, water: 0.5, grass: 0.5, dragon: 0.5 },
    grass: { water: 2, ground: 2, rock: 2, fire: 0.5, grass: 0.5, poison: 0.5, flying: 0.5, bug: 0.5, dragon: 0.5, steel: 0.5 },
    electric: { water: 2, flying: 2, electric: 0.5, grass: 0.5, dragon: 0.5, ground: 0 },
    ice: { grass: 2, ground: 2, flying: 2, dragon: 2, fire: 0.5, water: 0.5, ice: 0.5, steel: 0.5 },
    fighting: { normal: 2, ice: 2, rock: 2, dark: 2, steel: 2, poison: 0.5, flying: 0.5, psychic: 0.5, bug: 0.5, fairy: 0.5, ghost: 0 },
    ground: { fire: 2, electric: 2, poison: 2, rock: 2, steel: 2, grass: 0.5, bug: 0.5, flying: 0 },
    psychic: { fighting: 2, poison: 2, psychic: 0.5, steel: 0.5, dark: 0 },
    rock: { fire: 2, ice: 2, flying: 2, bug: 2, fighting: 0.5, ground: 0.5, steel: 0.5 },
  };

  const threats = Object.keys(CHART).filter((attacking) => {
    // Best case across the team: the lowest multiplier anybody takes.
    const best = Math.min(
      ...team.map((member) =>
        member.types.reduce((mult, defending) => mult * (CHART[attacking]?.[defending] ?? 1), 1),
      ),
    );
    return best > 1;
  });

  if (threats.length === 0) {
    return (
      <div className="tiny muted" style={{ marginTop: 4 }}>
        Somebody on this team resists every common attacking type.
      </div>
    );
  }

  return (
    <div className="tiny" style={{ marginTop: 4 }}>
      <span className="muted">Nobody resists: </span>
      {threats.map((type) => (
        <span key={type} className="chip chip--sm" style={{ marginRight: 3 }}>
          {type}
        </span>
      ))}
    </div>
  );
}
